import React, { createContext, useState, useContext, useEffect } from "react";
import { ClockOutComment } from "../../scenes/time-tracking/ClockOutComment";
import { API_GET, API_POST, DBRequest, useConfig } from "../../api/api";
import Loader from "../special/Loader";
import moment from "moment";
import { useTranslation } from "react-i18next";

const TimeProvider = createContext();

export const AppTimeTrackingProvider = ({ children, debug }) => {
  const config = useConfig();
  const { t } = useTranslation();

  const [activeProject, setActiveProject] = useState(null);
  const [secondsIn, setSecondsIn] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // initial
  useEffect(() => {
    // get active project
    DBRequest({
      config,
      path: "activeproject",
      method: API_GET,
      onResponse: handleActiveProject,
    });

    // get user Data
    DBRequest({
      config,
      path: "user/timetracking",
      method: API_GET,
      onResponse: handleTimeTrackingUser,
    });
  }, [config]);

  function handleActiveProject(response) {
    setActiveProject(response.data);
  }

  // clock in
  function clockIn(project, workPackage) {
    // request clock in
    const data = {
      projectId: project.id,
      workPackageId: workPackage ? workPackage.id : null,
    };
    DBRequest({
      config,
      path: "clockIn",
      method: API_POST(data),
      onError: handleResetData,
    });

    // set clock in time
    const clockInDateTime = moment();
    const formattedclockInDateTime = clockInDateTime.format();

    // set active project
    setActiveProject({
      ...project,
      activeWorkPackage: workPackage,
      clockInTime: formattedclockInDateTime,
    });
  }

  // clock out
  function clockOut() {
    if (Number(config.employee.requireCommentOnClockout)) {
      setOpenComment(true);
    } else {
      handleClockOut("");
    }
  }

  const handleClockOut = (comment) => {
    setOpenComment(false);
    DBRequest({
      config,
      path: "clockOut",
      method: API_POST({ comment }),
      onResponse: handleResetData,
      onLoading: setIsProcessing,
    });
  };

  const handleResetData = () => {
    DBRequest({
      config,
      path: "user/timetracking",
      method: API_GET,
      onResponse: handleTimeTrackingUser,
    });
    setSecondsIn(0);
    setActiveProject(null);
  };

  const [openComment, setOpenComment] = useState(false);

  // CLOCK ----------------------
  useEffect(() => {
    // start clock only if there is a active project
    if (!activeProject) return;

    // init loading times (no delay)
    setSecondsIn(updateTimeInSeconds(activeProject.clockInTime));

    // start clock
    var debugCounter = 0;
    const tickInterval = setInterval(() => {
      debugCounter += 60;
      if (activeProject) {
        const newSecondsIn = updateTimeInSeconds(
          activeProject.clockInTime,
          debugCounter
        );
        setSecondsIn(newSecondsIn);
      }
    }, 1000);
    return () => clearInterval(tickInterval);
  }, [activeProject]);

  function updateTimeInSeconds(startDateTime, debugCounter) {
    const timeDiff = moment().diff(moment(startDateTime));
    const seconds = Math.round(timeDiff / 1000);
    if (debug) return debugCounter + seconds;
    return seconds;
  }

  // DASHBOARD
  const [minutesPerDay, setMinutesPerDay] = useState(null);
  const [timeAccountDB, setTimeAccountDB] = useState(null);
  function handleTimeTrackingUser(data) {
    setMinutesToday(calcTimeToday(data.data.projectsToday));
    setMinutesPerDay(data.data.minutesPerDay);
    setTimeAccountDB(data.data.timeAccount);
  }
  // time today
  const [minutesToday, setMinutesToday] = useState(null);
  function calcTimeToday(projects) {
    var newMinutesToday = 0;

    projects.forEach((project) => {
      newMinutesToday += project.y;
    });

    return newMinutesToday;
  }

  return (
    <TimeProvider.Provider
      value={{
        activeProject,
        setActiveProject,
        clockOut,
        clockIn,
        secondsIn,
        minutesToday: minutesToday + Math.round(secondsIn / 60),
        workCompletionPercentage: minutesPerDay
          ? (minutesToday + Math.round(secondsIn / 60)) / minutesPerDay
          : null,
        timeAccount: timeAccountDB + Math.round(secondsIn / 60),
      }}
    >
      {children}
      <ClockOutComment
        open={openComment}
        onClose={() => setOpenComment(false)}
        onSubmit={handleClockOut}
      />
      <Loader active={isProcessing} message={t("Processing") + "..."} />
    </TimeProvider.Provider>
  );
};

export const useTimeTracking = () => useContext(TimeProvider);
